import React, { useState, useEffect } from 'react';
import {
  Download,
  TrendingUp,
  Users,
  DollarSign,
  FileText,
  Target,
  Globe,
  BarChart3
} from 'lucide-react';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import { supabase } from '../../lib/supabase';

const Reports: React.FC = () => {
  const [selectedReport, setSelectedReport] = useState<string>('overview');
  const [dateRange, setDateRange] = useState<string>('last-30-days');
  const [loading, setLoading] = useState(false);

  // Dashboard Metrics
  const [metrics, setMetrics] = useState({
    totalStudents: 0,
    totalApplications: 0,
    totalRevenue: 0,
    conversionRate: 0
  });

  // Chart Data
  const [overviewData, setOverviewData] = useState<any[]>([]);
  const [countryData, setCountryData] = useState<any[]>([]);
  const [performanceData, setPerformanceData] = useState<any[]>([]);

  const reportTypes = [
    { id: 'overview', name: 'Genel Bakış', icon: TrendingUp }, // Changed icon to TrendingUp as BarChart3 was unused
    { id: 'students', name: 'Öğrenci Raporları', icon: Users },
    { id: 'financial', name: 'Mali Raporlar', icon: DollarSign },
    { id: 'applications', name: 'Başvuru Raporları', icon: FileText },
    { id: 'performance', name: 'Performans Raporları', icon: Target },
    { id: 'geographic', name: 'Coğrafi Raporlar', icon: Globe }
  ];

  useEffect(() => {
    fetchReportData();
  }, [dateRange]);

  const fetchReportData = async () => {
    setLoading(true);
    try {
      if (!supabase) return;

      // 1. Fetch Students
      const { data: students, error: studentsError } = await supabase
        .from('students')
        .select('id, nationality, created_at, unit_type');

      if (studentsError) console.error('Error fetching students:', studentsError);

      // 2. Fetch Leads (Applications)
      const { data: leads, error: leadsError } = await supabase
        .from('leads')
        .select('id, created_at, status');

      if (leadsError) console.error('Error fetching leads:', leadsError);

      // 3. Fetch Revenue (Paid Installments)
      const { data: installments, error: installmentsError } = await supabase
        .from('installments')
        .select('amount, currency, payment_date')
        .eq('payment_status', 'paid');

      if (installmentsError) console.error('Error fetching installments:', installmentsError);

      // Calculate Metrics
      const totalStudents = students?.length || 0;
      const totalApplications = leads?.length || 0;

      let totalRevenue = 0;
      if (installments) {
        // Simple conversion for demo - in real app use convertCurrency utils
        for (const item of installments) {
          let amount = Number(item.amount);
          if (item.currency === 'TRY') amount = amount / 35; // Approx rate
          else if (item.currency === 'USD') amount = amount * 0.92;
          totalRevenue += amount;
        }
      }

      const conversionRate = totalApplications > 0
        ? ((students?.filter((s: any) => leads?.some((l: any) => l.id === s.id || l.status === 'converted'))?.length || 0) / totalApplications * 100)
        : 0;

      setMetrics({
        totalStudents,
        totalApplications,
        totalRevenue,
        conversionRate: parseFloat(conversionRate.toFixed(1))
      });

      // Calculate Monthly Trend (Last 6 Months)
      const months = [];
      const trMonths = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
      const now = new Date();

      for (let i = 5; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const monthIdx = d.getMonth();
        const monthName = trMonths[monthIdx];
        const year = d.getFullYear();

        // Filter data for this month
        const monthStudents = students?.filter((s: any) => {
          const sd = new Date(s.created_at);
          return sd.getMonth() === monthIdx && sd.getFullYear() === year;
        }).length || 0;

        const monthApps = leads?.filter((l: any) => {
          const ld = new Date(l.created_at);
          return ld.getMonth() === monthIdx && ld.getFullYear() === year;
        }).length || 0;

        months.push({
          month: monthName,
          students: monthStudents,
          applications: monthApps,
          // Revenue could be added here similarly
        });
      }
      setOverviewData(months);

      // Calculate Country Distribution
      const countryCounts: Record<string, number> = {};
      students?.forEach((s: any) => {
        const country = s.nationality || 'Diğer';
        countryCounts[country] = (countryCounts[country] || 0) + 1;
      });

      const chartColors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#6366F1'];
      const topCountries = Object.entries(countryCounts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([name, value], index) => ({
          name,
          value,
          color: chartColors[index % chartColors.length]
        }));
      setCountryData(topCountries);

      // Calculate Unit Performance
      // Mapping unit types to display names and simplified targets
      const unitMap: Record<string, { label: string, target: number }> = {
        'education_consultant': { label: 'Eğitim Danışmanlığı', target: 20 },
        'visa_consultant': { label: 'Vize Danışmanlığı', target: 15 },
        'marketing': { label: 'Pazarlama', target: 50 },
        'sales': { label: 'Satış', target: 25 },
        'customer_service': { label: 'Müşteri Hizmetleri', target: 10 }
      };

      const unitCounts: Record<string, number> = {};
      students?.forEach((s: any) => {
        if (s.unit_type && unitMap[s.unit_type]) {
          unitCounts[s.unit_type] = (unitCounts[s.unit_type] || 0) + 1;
        }
      });

      const perfData = Object.entries(unitMap).map(([key, info]) => {
        const achieved = unitCounts[key] || 0;
        const percentage = Math.min(100, Math.round((achieved / info.target) * 100));
        return {
          unit: info.label,
          target: info.target,
          achieved,
          percentage
        };
      }).filter(u => u.achieved > 0 || u.target > 0); // Only show active units or those with targets

      setPerformanceData(perfData);

    } catch (error) {
      console.error('Rapor verileri alınırken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('de-DE', { // Using EUR formatting generally
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const renderOverviewReport = () => (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
          <div className="flex items-center space-x-3">
            <Users className="w-8 h-8 text-blue-500" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{metrics.totalStudents}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Toplam Öğrenci</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
          <div className="flex items-center space-x-3">
            <FileText className="w-8 h-8 text-green-500" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{metrics.totalApplications}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Toplam Başvuru</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
          <div className="flex items-center space-x-3">
            <DollarSign className="w-8 h-8 text-purple-500" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{formatCurrency(metrics.totalRevenue)}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Toplam Gelir (Tahmini)</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
          <div className="flex items-center space-x-3">
            <TrendingUp className="w-8 h-8 text-yellow-500" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">%{metrics.conversionRate}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Dönüşüm Oranı</p>
            </div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Aylık Trend (Son 6 Ay)</h3>
          {loading ? (
            <div className="h-[300px] flex items-center justify-center">Yükleniyor...</div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={overviewData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="students" stroke="#3B82F6" name="Öğrenci" />
                <Line type="monotone" dataKey="applications" stroke="#10B981" name="Başvuru" />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Ülke Dağılımı (Öğrenciler)</h3>
          {loading ? (
            <div className="h-[300px] flex items-center justify-center">Yükleniyor...</div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={countryData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {countryData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );

  const renderPerformanceReport = () => (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Birim Performansları (Aktif Öğrenci Sayısı)</h3>
        {loading ? (
          <div className="py-10 text-center">Yükleniyor...</div>
        ) : (
          <div className="space-y-4">
            {performanceData.map((unit, index) => (
              <div key={index} className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-gray-900 dark:text-white">{unit.unit}</h4>
                  <span className={`text-sm font-medium ${unit.percentage >= 90 ? 'text-green-600 dark:text-green-400' :
                    unit.percentage >= 80 ? 'text-yellow-600 dark:text-yellow-400' :
                      'text-gray-600 dark:text-gray-400'
                    }`}>
                    {unit.achieved} / {unit.target} (%{unit.percentage})
                  </span>
                </div>
                {unit.target > 0 && (
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${unit.percentage >= 90 ? 'bg-green-500' :
                        unit.percentage >= 80 ? 'bg-yellow-500' :
                          unit.percentage >= 50 ? 'bg-blue-500' : 'bg-red-500'
                        }`}
                      style={{ width: `${unit.percentage}%` }}
                    />
                  </div>
                )}
              </div>
            ))}
            {performanceData.length === 0 && (
              <p className="text-center text-gray-500">Henüz performans verisi bulunmuyor.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );

  const renderContent = () => {
    switch (selectedReport) {
      case 'overview':
        return renderOverviewReport();
      case 'performance':
        return renderPerformanceReport();
      default:
        return (
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-8 text-center">
            <BarChart3 className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Rapor Geliştiriliyor</h3>
            <p className="text-gray-600 dark:text-gray-400">Bu rapor türü yakında kullanıma sunulacak.</p>
          </div>
        );
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Raporlar</h2>
          <p className="text-gray-600 dark:text-gray-400">Detaylı analiz ve raporlar</p>
        </div>
        <div className="flex items-center space-x-3">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg px-4 py-2 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="last-7-days">Son 7 Gün</option>
            <option value="last-30-days">Son 30 Gün</option>
            <option value="last-3-months">Son 3 Ay</option>
            <option value="last-6-months">Son 6 Ay</option>
            <option value="last-year">Son 1 Yıl</option>
          </select>
          <button className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors">
            <Download className="w-4 h-4" />
            <span>İndir</span>
          </button>
        </div>
      </div>

      {/* Report Types */}
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {reportTypes.map((report) => {
            const IconComponent = report.icon;
            return (
              <button
                key={report.id}
                onClick={() => setSelectedReport(report.id)}
                className={`flex flex-col items-center space-y-2 p-4 rounded-lg transition-colors ${selectedReport === report.id
                  ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}
              >
                <IconComponent className="w-6 h-6" />
                <span className="text-sm font-medium text-center">{report.name}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Report Content */}
      {renderContent()}
    </div>
  );
};

export default Reports;